import { ApiRequest } from '../baseService'

const BaseRoute = 'app/pdf'

/**
 * Check health status of Document Redaction Service
 * @returns Health status object
 */
function CheckHealth() {
  return ApiRequest.get(`${BaseRoute}/redact/health`)
}

/**
 * Detect PII in PDF document
 * @param file PDF file to analyze
 * @param instruction Redaction instruction (optional)
 * @param languages Languages to detect (optional)
 * @returns JSON object with detected PII
 */
function DetectPii(file: File, instruction?: string, languages?: string) {
  const formData = new FormData()
  formData.append('file', file)
  if (instruction) {
    formData.append('instruction', instruction)
  }
  if (languages) {
    formData.append('languages', languages)
  }

  return ApiRequest.post(`${BaseRoute}/redact/detect-pii`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

/**
 * Apply redaction to PDF document with specific PII targets
 * @param file PDF file to redact
 * @param targets Array of PII targets to redact
 * @param mode Redaction mode (optional, default: 'redact')
 * @param languages Languages to detect (optional)
 * @returns PDF file blob
 */
function ApplyRedaction(
  file: File,
  targets: any[],
  mode?: string,
  languages?: string
) {
  const formData = new FormData()
  formData.append('file', file)

  // Append targets in indexed format for ASP.NET Core model binding
  targets.forEach((target, index) => {
    Object.keys(target).forEach(key => {
      const value = target[key]
      if (value !== null && value !== undefined) {
        formData.append(`Targets[${index}].${key}`, value.toString())
      }
    })
  })

  if (mode) {
    formData.append('mode', mode)
  }
  if (languages) {
    formData.append('languages', languages)
  }

  return ApiRequest.post(`${BaseRoute}/redact/apply`, formData, {
    responseType: 'blob',
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

/**
 * Redact PDF document by specific rectangle coordinates
 * @param file PDF file to redact
 * @param rectangles Array of { page_number, rects: [{ x, y, w, h }] }
 * @param mode Redaction mode (optional, default: 'redact')
 * @returns PDF file blob
 */
function RedactByRectangles(
  file: File,
  rectangles: {
    page_number: number
    rects: { x: number; y: number; w: number; h: number }[]
  }[],
  mode?: string
) {
  const formData = new FormData()
  formData.append('file', file)

  // Flatten to nested indexed format for ASP.NET Core model binding:
  // Rectangles[0].PageNumber, Rectangles[0].PageWidth, Rectangles[0].PageHeight
  // Rectangles[0].Rects[0].X, Rectangles[0].Rects[0].Y, ...
  // Coordinates are in standard PDF space (Y-up, bottom-left origin, PDF points).
  rectangles.forEach((page, pageIndex) => {
    formData.append(
      `Rectangles[${pageIndex}].PageNumber`,
      page.page_number.toString()
    )
    // formData.append(`Rectangles[${pageIndex}].PageWidth`, page.page_width.toString())
    // formData.append(`Rectangles[${pageIndex}].PageHeight`, page.page_height.toString())

    page.rects.forEach((rect, rectIndex) => {
      formData.append(
        `Rectangles[${pageIndex}].Rects[${rectIndex}].X`,
        rect.x.toString()
      )
      formData.append(
        `Rectangles[${pageIndex}].Rects[${rectIndex}].Y`,
        rect.y.toString()
      )
      formData.append(
        `Rectangles[${pageIndex}].Rects[${rectIndex}].W`,
        rect.w.toString()
      )
      formData.append(
        `Rectangles[${pageIndex}].Rects[${rectIndex}].H`,
        rect.h.toString()
      )
    })
  })

  if (mode) {
    formData.append('mode', mode)
  }

  return ApiRequest.post(`${BaseRoute}/redact/by-rectangles`, formData, {
    responseType: 'blob',
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

const API = {
  CheckHealth,
  DetectPii,
  ApplyRedaction,
  RedactByRectangles,
}

export default API
